import subDays from "date-fns/subDays";
import React, { useRef, useState } from "react";
import { RCCBadge, RCCButton, RCCGrid, RCCInput, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import { IRefIntegrationsLog } from "../interface";
import ListIntegrationLogs from "./listIntegrationLogs";

const ListIntegratedFields: React.FC = () => {

	const { translate, currentLanguage } = useTranslation();
	const { superUser } = useAuth();

	const paginationRef = useRef<IForwardPagination>(null);
	const integrationLogsRef = useRef<IRefIntegrationsLog>(null);


	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string, group: string, situation: string, endDate: Date | null, initDate: Date | null}>({ group: "", filter: "", situation:"-1", endDate: new Date(), initDate: subDays(new Date(),30) });
	const { handleSubmit : handleSubmitFilters, setValue: setValueFilters, control: controlFilters, register : registerFilters, reset: resetFilters, watch: watchFilters } = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as any;
			newState.filter =  data.filter;
			newState.situation = data.situation.value;
			newState.endDate = data.endDate;
			newState.initDate = data.initDate;
			if(data.group)
				newState.group = typeof data.group === "object" ? data.group?.value : data.group;
		
			return newState;
		});
		
		paginationRef.current?.reload();
	};

	const colSituacao = (row: any) => (
		<RCCBadge color={row.iql004Situacao === 0 ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.iql004Situacao === 0 ? translate("Sucesso") : translate("Falha")}
		</RCCBadge>
	);

	const colLogs = (row: any) => (
		<>
			<RCCTooltip
				color="blue"
				text={translate("Visualizar Logs de Integração")}
				position="left"	
			>
				<RCCButton
					color="blue"
					icon={{
						name: "stack"
					}}
					layout="isRounded"
					 onClick={() => integrationLogsRef.current?.actionButtonOpen(row.iqb001Id, row.iqb001Descricao)}
				/>
			</RCCTooltip>		
		</>
	);

	const convertToInt = (row: string) => {
		const converted = parseInt(row, 10);
		if(Number(converted) || converted === 0) 
			return converted;
		return -1;
	};

	return (
		<>
    	<RCCGrid
				container
				item
				style={{ marginTop: "3rem" }}
			>
				<PageTitle
					title={translate("Campos Integrados")}
					icon={{
						name: "tools"
					}}
				>
					<RCCTooltip
						text={translate("Filtros")}
					  color={showFilters || filters.filter || filters.group || filters.initDate || filters.endDate || filters.situation !== "-1" ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || filters.filter || filters.group || filters.initDate || filters.endDate || filters.situation !== "-1"  ? "blue" : "grey"}
							layout="isRounded"
							size="md"
						  onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>
			</RCCGrid>
    	{/* Filters */}
			{showFilters && (
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ group: "", filter: "", situation: {value: "-1", label: translate("Todos")} , endDate: null, initDate: null })}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
							xs={10}
						>

							{superUser ? (
								<RCCGrid
									container 
									item
									xs={12}
									sm={6}
									md={4}
									lg={4}
								>
									<LovBusinessGroups 
										control={controlFilters}
										name="group" 
										defaultValue={filters.group ?? ""}
										label={translate("Grupo de Empresas")}
										submit="value and label"
									/>
								</RCCGrid>
							) : null}

							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
								lg={4}
							>
								<DateTimePickerRange 
									type="datePicker"
									label={translate("Período")}
									labelSeparator={translate("até")}
									nameInit="initDate"
									nameEnd="endDate"
									setValue={setValueFilters}
									control={controlFilters}
									defaultValueEnd={filters.endDate}
									defaultValueInit={filters.initDate}	
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
								lg={3}
							>
								<Select 
									control={controlFilters}
									name="situation" 
									options={[
										{value: "1", label: translate("Falha")}, 
										{value: "0", label: translate("Sucesso")},
										{value: "-1", label: translate("Todos")}
									]} 
									label={translate("Situação")}
									defaultValue={{value: "S", label: translate("Sim")}}
									placeholder={translate("Selecionar Opção")}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
								md={4}
								lg={4}
							>
								<RCCInput
									name="filter" label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
			)}
      
			<Pagination 
				ref={paginationRef}
				dataOrRoute="Integrations/ListIntegratedFields" 
				filtersData={{
					...filters,
					initDate: filters.initDate ? filters.initDate.toLocaleDateString() : "",
					endDate: filters.endDate ? filters.endDate.toLocaleDateString() : "",
					situation: filters.situation ? convertToInt(filters.situation) : -1 
				}} 
				columns={[
					{
						idColumn: "iqb001Descricao",
						nameColumn: translate("Campo"),
						xs: 12,
						sm: 5,
						justify: "left",
						type: "string",
						cellRenderer: (cell: any) => `${cell.iqb001Descricao} (${cell.iqb001Codigo})`,
						truncate: true
					},
					{
						idColumn: "iql004Data",
						nameColumn: translate("Última Integração"),
						xs: 12,
						sm: 5,
						justify: "left",
						type: "string",
						cellRenderer: (cell: any) => formatDate(cell.iql004Data, currentLanguage, true),
						truncate: true
					},
					{
						idColumn: "iql004Situacao",
						nameColumn: translate("Situação"),
						xs: 12,
						sm: 1,
						justify: "left",
						type: "string",
						cellRenderer: colSituacao,
						truncate: true
					},
					{
						idColumn: "options",
						nameColumn: " ",
						justify: "right",
						xs: 12,
						sm: 1,
						cellRenderer: colLogs,
						disableSort: true
					}
				]}			
			/>


			<ListIntegrationLogs 
				ref={integrationLogsRef}
			/>


		</>
	);
};

export default ListIntegratedFields;
